import { createSelector } from 'reselect';
import type { VersionId } from '../../common/model';
import { MOVE, DELETE } from '../model/operation';
import { AFFECTS, FIXED_IN } from '../model/system-field';
import type { FieldId, FieldOperation, Operation, Field } from '../model/types';
import type { State } from './types';

export const getBaseUrl = (state: State): string => state.baseUrl;
export const getProjectKey = (state: State): string => state.projectKey;
export const getVersionName = (state: State): string => state.version.name;
export const getVersionId = (state: State): VersionId => state.version.id;
export const getSuccessCallback = (
	state: State,
): ((arg1: { movedRelatedIssuesTo: VersionId | undefined }) => void) => state.onSuccess;
export const getErrorCallback = (state: State): ((arg1: Error) => void) => state.onError;
export const getCancelCallback = (state: State): (() => void) => state.onCancel;
export const getModalShownCallback = (state: State): (() => void) => state.onOpened;

export const getFields = (state: State): FieldOperation[] => Object.values(state.fields);

export const getSortedFields = createSelector([getFields], (fields) => {
	const fieldIdx = (field: Field): number => {
		if (field.id === FIXED_IN.id) {
			return 2;
		}
		if (field.id === AFFECTS.id) {
			return 1;
		}
		return 0;
	};

	const sortFields = (fo1: FieldOperation, fo2: FieldOperation): number =>
		fieldIdx(fo2.relatedField.field) - fieldIdx(fo1.relatedField.field);

	return fields.sort(sortFields);
});

export const getFieldOperation = (state: State, id: FieldId): Operation | undefined =>
	state.fields[id] && state.fields[id].operation;

export const isFieldVersionPickerDisabled = (state: State, id: FieldId): boolean =>
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	state.inProgress || getFieldOperation(state as State, id as FieldId) === DELETE;

export const getFieldTargetVersion = (state: State, id: FieldId): VersionId | undefined =>
	state.fields[id] && state.fields[id].targetVersion;

export const getTargetVersionForFixVersionField = (state: State): VersionId | undefined =>
	getFieldOperation(state, FIXED_IN.id) === MOVE
		? getFieldTargetVersion(state, FIXED_IN.id)
		: undefined;
export const isDialogVisible = (state: State): boolean => state.isVisible;

export const isSwapValid = createSelector([getFields], (fields) =>
	fields.reduce(
		(valid, { operation, targetVersion }) =>
			valid && (operation !== MOVE || Boolean(targetVersion)),
		true,
	),
);

export const isInProgress = (state: State): boolean => state.inProgress;
